'use client';

import { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { Command } from 'cmdk';
import { Search, Calendar, ShoppingCart, UserPlus, Receipt, Home, Users, Briefcase, Package, DollarSign, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/lib/integrations/language';
import { useAuth } from '@/lib/integrations/supabase';
import { useCustomerSearch } from '@/lib/features/swr';
import { looksLikePhoneNumber } from '@/lib/utils/phone';
import { getActionsForRole, searchActions, type AppAction } from '@/lib/features/actions/registry';
import { exportBookings, exportSales } from '@/lib/features/actions/export-handlers';
import { useToast } from '@/components/shared/ui/toast';
import './command-palette.css';

export function CommandPalette() {
  const { t } = useTranslation('chat');
  const { isRTL } = useLanguage();
  const router = useRouter();
  const { profile } = useAuth();
  const { addToast } = useToast();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Detect phone number pattern
  const isPhoneQuery = /^\d{9,}$/.test(query);

  // Search customers if query looks like a phone number
  const { data: customerResults = [], isLoading: customersLoading } = useCustomerSearch(
    isPhoneQuery ? profile?.business_id || null : null,
    isPhoneQuery ? query : ''
  );

  // Filter actions based on user role and query
  const filteredActions = useMemo(() => {
    if (!profile) return [];
    if (!query) return getActionsForRole(profile.role);
    return searchActions(query, profile.role);
  }, [query, profile]);

  // Keyboard shortcut + custom event listener
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    const handleCustomOpen = () => {
      setOpen(true);
    };

    document.addEventListener('keydown', down);
    document.addEventListener('openCommandPalette', handleCustomOpen);
    return () => {
      document.removeEventListener('keydown', down);
      document.removeEventListener('openCommandPalette', handleCustomOpen);
    };
  }, []);

  // Reset query when closing
  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  // Execute action
  const executeAction = async (action: AppAction) => {
    // Handle export actions specially
    if (action.id === 'export-bookings') {
      setIsExporting(true);
      try {
        await exportBookings('week');
        addToast({ message: t('commandPalette.exportSuccess'), type: 'success' });
      } catch (error) {
        addToast({ message: t('commandPalette.exportError'), type: 'error' });
      } finally {
        setIsExporting(false);
      }
      setOpen(false);
      return;
    }

    if (action.id === 'export-sales') {
      setIsExporting(true);
      try {
        await exportSales('week');
        addToast({ message: t('commandPalette.exportSuccess'), type: 'success' });
      } catch (error) {
        addToast({ message: t('commandPalette.exportError'), type: 'error' });
      } finally {
        setIsExporting(false);
      }
      setOpen(false);
      return;
    }

    if (action.route) {
      router.push(action.route);
    } else if (action.handler) {
      action.handler();
    }
    setOpen(false);
  };

  // Navigate to customer
  const navigateToCustomer = (customerId: string) => {
    router.push(`/customers?id=${customerId}`);
    setOpen(false);
  };

  // Natural language shortcuts
  const naturalShortcuts: Record<string, string> = {
    refund: '/sales?filter=refund',
    pending: '/schedule?status=pending',
    tomorrow: `/schedule?date=${new Date(Date.now() + 86400000).toISOString().split('T')[0]}`,
    today: '/schedule?date=today',
    yesterday: `/schedule?date=${new Date(Date.now() - 86400000).toISOString().split('T')[0]}`,
  };

  const shortcutRoute = naturalShortcuts[query.toLowerCase()];

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label="Command Palette"
      className={`command-palette ${isRTL ? 'rtl' : ''}`}
    >
      <div className={`command-palette-header ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Search className="command-palette-search-icon" />
        <Command.Input
          value={query}
          onValueChange={setQuery}
          placeholder={t('commandPalette.placeholder')}
          className={`command-palette-input ${isRTL ? 'text-right' : ''}`}
          dir={isRTL ? 'rtl' : 'ltr'}
        />
      </div>

      <Command.List className="command-palette-list">
        <Command.Empty className="command-palette-empty">
          {t('commandPalette.noResults')}
        </Command.Empty>

        {/* Natural language shortcut */}
        {shortcutRoute && (
          <Command.Group heading={t('commandPalette.groups.quickJump')}>
            <Command.Item
              onSelect={() => {
                router.push(shortcutRoute);
                setOpen(false);
              }}
              className={`command-palette-item ${isRTL ? 'flex-row-reverse text-right' : ''}`}
            >
              <Home className="command-palette-item-icon" />
              <span>{t('commandPalette.goTo', { destination: query })}</span>
            </Command.Item>
          </Command.Group>
        )}

        {/* Customer search results (phone number) */}
        {isPhoneQuery && (
          <Command.Group heading={t('commandPalette.groups.customers')}>
            {customersLoading ? (
              <Command.Item disabled className={`command-palette-item ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                <Loader2 className="command-palette-item-icon animate-spin" />
                <span>{t('commandPalette.searching')}</span>
              </Command.Item>
            ) : customerResults.length > 0 ? (
              customerResults.slice(0, 5).map((customer) => (
                <Command.Item
                  key={customer.id}
                  onSelect={() => navigateToCustomer(customer.id)}
                  className={`command-palette-item ${isRTL ? 'flex-row-reverse text-right' : ''}`}
                >
                  <Users className="command-palette-item-icon" />
                  <div className={`flex flex-col ${isRTL ? 'items-end' : 'items-start'}`}>
                    <span dir={looksLikePhoneNumber(customer.name) ? 'ltr' : undefined}>{customer.name}</span>
                    <span className="text-xs text-muted-foreground text-left" dir="ltr">{customer.phone}</span>
                  </div>
                </Command.Item>
              ))
            ) : (
              <Command.Item disabled className={`command-palette-item ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                <span>{t('commandPalette.noCustomers')}</span>
              </Command.Item>
            )}
          </Command.Group>
        )}

        {/* Create Actions */}
        {filteredActions.some((a) => a.category === 'create') && (
          <Command.Group heading={t('commandPalette.groups.create')}>
            {filteredActions
              .filter((a) => a.category === 'create')
              .map((action) => (
                <Command.Item
                  key={action.id}
                  onSelect={() => executeAction(action)}
                  className={`command-palette-item ${isRTL ? 'flex-row-reverse text-right' : ''}`}
                >
                  <action.icon className="command-palette-item-icon" />
                  <span>{action.translationKey ? t(action.translationKey) : action.label}</span>
                </Command.Item>
              ))}
          </Command.Group>
        )}

        {/* Navigate Actions */}
        {filteredActions.some((a) => a.category === 'navigate') && (
          <Command.Group heading={t('commandPalette.groups.navigate')}>
            {filteredActions
              .filter((a) => a.category === 'navigate')
              .map((action) => (
                <Command.Item
                  key={action.id}
                  onSelect={() => executeAction(action)}
                  className={`command-palette-item ${isRTL ? 'flex-row-reverse text-right' : ''}`}
                >
                  <action.icon className="command-palette-item-icon" />
                  <span>{action.translationKey ? t(action.translationKey) : action.label}</span>
                </Command.Item>
              ))}
          </Command.Group>
        )}

        {/* Search Actions */}
        {filteredActions.some((a) => a.category === 'search') && (
          <Command.Group heading={t('commandPalette.groups.search')}>
            {filteredActions
              .filter((a) => a.category === 'search')
              .map((action) => (
                <Command.Item
                  key={action.id}
                  onSelect={() => executeAction(action)}
                  className={`command-palette-item ${isRTL ? 'flex-row-reverse text-right' : ''}`}
                >
                  <action.icon className="command-palette-item-icon" />
                  <span>{action.translationKey ? t(action.translationKey) : action.label}</span>
                </Command.Item>
              ))}
          </Command.Group>
        )}

        {/* Export Actions */}
        {filteredActions.some((a) => a.category === 'export') && (
          <Command.Group heading={t('commandPalette.groups.export')}>
            {filteredActions
              .filter((a) => a.category === 'export')
              .map((action) => (
                <Command.Item
                  key={action.id}
                  onSelect={() => executeAction(action)}
                  className={`command-palette-item ${isRTL ? 'flex-row-reverse text-right' : ''}`}
                >
                  <action.icon className="command-palette-item-icon" />
                  <span>{action.translationKey ? t(action.translationKey) : action.label}</span>
                </Command.Item>
              ))}
          </Command.Group>
        )}
      </Command.List>

      <div className="command-palette-footer">
        <div className={`text-xs text-muted-foreground flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <span>{isRTL ? `${t('commandPalette.footer.navigate')} ↑↓` : `↑↓ ${t('commandPalette.footer.navigate')}`}</span>
          <span>{isRTL ? `${t('commandPalette.footer.select')} ↵` : `↵ ${t('commandPalette.footer.select')}`}</span>
          <span>{isRTL ? `${t('commandPalette.footer.close')} Esc` : `Esc ${t('commandPalette.footer.close')}`}</span>
        </div>
      </div>
    </Command.Dialog>
  );
}
